import { listCategories } from '@wix/ambassador-faq-category-v2-category/http';
import { SortOrder } from '@wix/ambassador-faq-category-v2-category/types';
import {
  listQuestionEntries,
  queryQuestionEntries,
} from '@wix/ambassador-faq-question-entry-v2-question-entry/http';
import {
  ContentFormat,
  QueryQuestionEntriesRequest,
  QueryQuestionEntriesRequestContentFormat,
  QuestionEntry,
} from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { IHttpClient } from '@wix/http-client';
import { draftjsToPlainText } from '../../utils/draftjsToPlainText';

export const getListCategoriesAPI = async (httpClient: IHttpClient) => {
  const response = await httpClient.request(listCategories({}));

  while (response.data.pagingMetadata?.hasNext) {
    const nextCursor = response.data.pagingMetadata.cursors?.next;

    const nextResponse = await httpClient.request(
      listCategories({
        paging: nextCursor ? { cursor: nextCursor } : undefined,
      }),
    );

    response.data.categories!.push(...nextResponse.data.categories!);
    response.data.pagingMetadata = nextResponse.data.pagingMetadata;
  }

  return response.data;
};
export const getListQuestionsAPI = async ({
  categoryId,
  cursor,
  limit = 50,
  httpClient,
  isSEO = false,
}: {
  categoryId?: string | null | undefined | (string | null | undefined)[];
  limit?: number;
  cursor?: string;
  httpClient: IHttpClient;
  isSEO?: boolean;
}) => {
  const requestPayload: QueryQuestionEntriesRequest = {
    query: {
      cursorPaging: { cursor, limit },
    },
    contentFormat: QueryQuestionEntriesRequestContentFormat.DRAFTJS,
  };

  if (!cursor && requestPayload.query) {
    requestPayload.query.filter = { categoryId };
    requestPayload.query.sort = [
      { fieldName: 'sortOrder', order: SortOrder.ASC },
    ];
  }
  const response = await httpClient.request(
    queryQuestionEntries(requestPayload),
  );
  if (isSEO) {
    while (response.data.pagingMetadata?.hasNext) {
      const nextCursor = response.data.pagingMetadata.cursors?.next;

      const nextResponse = await httpClient.request(
        listQuestionEntries({
          contentFormat: ContentFormat.DRAFTJS,
          paging: nextCursor ? { cursor: nextCursor } : undefined,
        }),
      );

      response.data.questionEntries!.push(
        ...nextResponse.data.questionEntries!,
      );
      response.data.pagingMetadata = nextResponse.data.pagingMetadata;
    }

    response.data.questionEntries = (response.data.questionEntries ?? []).map(
      (entry) => ({
        ...entry,
        // Clear draftjs so the Widget renders plainText as a string instead of
        // RicosViewer. RicosViewer uses Suspense/loadable plugins that render
        // null in SSR but content on the client, causing React hydration errors
        // (#418, #422). This restores pre-PR #2536 behaviour for SEO requests.
        draftjs: undefined,
        plainText: draftjsToPlainText(entry.draftjs) || entry.plainText || '',
      }),
    );
  }
  return response.data;
};
export const getAllQuestionsAPI = async ({
  categoryIds,
  httpClient,
}: {
  categoryIds: string | string[] | undefined;
  httpClient: IHttpClient;
}): Promise<QuestionEntry[]> => {
  const all: QuestionEntry[] = [];
  let cursor: string | undefined;

  do {
    const payload: QueryQuestionEntriesRequest = cursor
      ? {
          query: { cursorPaging: { cursor, limit: 100 } },
          contentFormat: QueryQuestionEntriesRequestContentFormat.DRAFTJS,
        }
      : {
          query: {
            filter: { categoryId: categoryIds },
            sort: [{ fieldName: 'sortOrder', order: SortOrder.ASC }],
            cursorPaging: { limit: 100 },
          },
          contentFormat: QueryQuestionEntriesRequestContentFormat.DRAFTJS,
        };
    const response = await httpClient.request(queryQuestionEntries(payload));
    all.push(...(response.data.questionEntries ?? []));
    cursor = response.data.pagingMetadata?.hasNext
      ? response.data.pagingMetadata.cursors?.next ?? undefined
      : undefined;
  } while (cursor);

  return all;
};
export const getQuestionAPI = async ({
  id,
  httpClient,
}: {
  id?: string | null | undefined | (string | null | undefined)[];
  httpClient: IHttpClient;
}) => {
  const response = await httpClient.request(
    queryQuestionEntries({
      query: {
        filter: { id },
        sort: [{ fieldName: 'sortOrder', order: SortOrder.ASC }],
      },
    }),
  );
  return response.data;
};
